import { Link2, Calendar, MessageCircle, AlertCircle, AlertTriangle, TrendingUp, Clock, CheckCircle2, Lightbulb, Ban } from 'lucide-react';
import { motion } from 'framer-motion';
import type { Ticket, TicketStatus, TicketType, TicketArtifact } from '../types';
import { Avatar } from './Avatar';
import { Markdown } from './Markdown';
import { CollapsibleText } from './CollapsibleText';
import { ProviderMark } from './ProviderMark';
import { normalizeComments } from '../lib/comments';
import { ActionBar } from './actions/ActionModal';

interface TicketCardProps {
  ticket: Ticket;
  viewMode?: 'list' | 'card' | 'large';
  onClick?: () => void;
}

// ── Priority ──────────────────────────────────────────────────────────────────

const PRIORITY_LABELS: Record<number, string> = {
  0: 'No Priority', 1: 'Urgent', 2: 'High', 3: 'Medium', 4: 'Low',
};
const PRIORITY_BADGE: Record<number, string> = {
  1: 'bg-red-100 text-red-700 border-red-200 dark:bg-red-900/20 dark:text-red-400 dark:border-red-900/30',
  2: 'bg-orange-100 text-orange-700 border-orange-200 dark:bg-orange-900/20 dark:text-orange-400 dark:border-orange-900/30',
  3: 'bg-yellow-100 text-yellow-700 border-yellow-200 dark:bg-yellow-900/20 dark:text-yellow-400 dark:border-yellow-900/30',
  4: 'bg-gray-100 text-gray-600 border-gray-200 dark:bg-white/5 dark:text-gray-400 dark:border-white/10',
};

// ── Status ────────────────────────────────────────────────────────────────────

interface StatusCfg {
  label: string;
  Icon: React.ComponentType<{ className?: string }>;
  // Badge style for use on the colored header background
  headerBadge: string;
  // Badge style for use in list view (neutral bg)
  listBadge: string;
  // Colored header section background
  headerBg: string;
  // Divider between header and body
  divider: string;
}

const STATUS_CONFIG: Record<TicketStatus, StatusCfg> = {
  triage: {
    label: 'Triage',
    Icon: AlertCircle,
    headerBg: 'bg-orange-100 dark:bg-orange-900/40',
    headerBadge: 'bg-white/80 text-orange-700 border-orange-300/60 dark:bg-black/25 dark:text-orange-300 dark:border-orange-700/50',
    listBadge: 'bg-orange-100 text-orange-700 border-orange-200 dark:bg-orange-900/20 dark:text-orange-300 dark:border-orange-900/30',
    divider: 'border-orange-200 dark:border-orange-900/50',
  },
  at_risk: {
    label: 'At Risk',
    Icon: AlertTriangle,
    headerBg: 'bg-red-100 dark:bg-red-900/40',
    headerBadge: 'bg-white/80 text-red-700 border-red-300/60 dark:bg-black/25 dark:text-red-300 dark:border-red-700/50',
    listBadge: 'bg-red-100 text-red-700 border-red-200 dark:bg-red-900/20 dark:text-red-300 dark:border-red-900/30',
    divider: 'border-red-200 dark:border-red-900/50',
  },
  on_track: {
    label: 'On Track',
    Icon: TrendingUp,
    headerBg: 'bg-yellow-100 dark:bg-yellow-900/40',
    headerBadge: 'bg-white/80 text-yellow-700 border-yellow-300/60 dark:bg-black/25 dark:text-yellow-300 dark:border-yellow-700/50',
    listBadge: 'bg-yellow-100 text-yellow-700 border-yellow-200 dark:bg-yellow-900/20 dark:text-yellow-300 dark:border-yellow-900/30',
    divider: 'border-yellow-200 dark:border-yellow-900/50',
  },
  planning: {
    label: 'Planning',
    Icon: Clock,
    headerBg: 'bg-blue-100 dark:bg-blue-900/40',
    headerBadge: 'bg-white/80 text-blue-700 border-blue-300/60 dark:bg-black/25 dark:text-blue-300 dark:border-blue-700/50',
    listBadge: 'bg-blue-100 text-blue-700 border-blue-200 dark:bg-blue-900/20 dark:text-blue-300 dark:border-blue-900/30',
    divider: 'border-blue-200 dark:border-blue-900/50',
  },
  complete: {
    label: 'Complete',
    Icon: CheckCircle2,
    headerBg: 'bg-green-100 dark:bg-green-900/40',
    headerBadge: 'bg-white/80 text-green-700 border-green-300/60 dark:bg-black/25 dark:text-green-300 dark:border-green-700/50',
    listBadge: 'bg-green-100 text-green-700 border-green-200 dark:bg-green-900/20 dark:text-green-300 dark:border-green-900/30',
    divider: 'border-green-200 dark:border-green-900/50',
  },
  idea: {
    label: 'Idea',
    Icon: Lightbulb,
    headerBg: 'bg-purple-100 dark:bg-purple-900/40',
    headerBadge: 'bg-white/80 text-purple-700 border-purple-300/60 dark:bg-black/25 dark:text-purple-300 dark:border-purple-700/50',
    listBadge: 'bg-purple-100 text-purple-700 border-purple-200 dark:bg-purple-900/20 dark:text-purple-300 dark:border-purple-900/30',
    divider: 'border-purple-200 dark:border-purple-900/50',
  },
  invalid: {
    label: 'Invalid',
    Icon: Ban,
    headerBg: 'bg-gray-100 dark:bg-gray-800/60',
    headerBadge: 'bg-white/80 text-gray-600 border-gray-300/60 dark:bg-black/25 dark:text-gray-400 dark:border-gray-600/50',
    listBadge: 'bg-gray-100 text-gray-500 border-gray-200 dark:bg-white/5 dark:text-gray-500 dark:border-white/10',
    divider: 'border-gray-200 dark:border-white/10',
  },
};

// ── Type badge ─────────────────────────────────────────────────────────────────

function getTypeBadge(type: TicketType) {
  const map: Record<TicketType, { label: string; classes: string }> = {
    feature:     { label: 'Feature',     classes: 'bg-violet-100 text-violet-700 border-violet-200 dark:bg-violet-900/20 dark:text-violet-300 dark:border-violet-900/30' },
    enhancement: { label: 'Enhancement', classes: 'bg-blue-100 text-blue-700 border-blue-200 dark:bg-blue-900/20 dark:text-blue-300 dark:border-blue-900/30' },
    bug_fix:     { label: 'Bug Fix',     classes: 'bg-red-100 text-red-700 border-red-200 dark:bg-red-900/20 dark:text-red-300 dark:border-red-900/30' },
    task:        { label: 'Task',        classes: 'bg-gray-100 text-gray-600 border-gray-200 dark:bg-white/5 dark:text-gray-400 dark:border-white/10' },
  };
  return map[type] ?? map.task;
}

// ── Artifact icon ─────────────────────────────────────────────────────────────

function ArtifactIcon({ artifact }: { artifact: TicketArtifact }) {
  let content: React.ReactNode;
  switch (artifact.type) {
    case 'jira':
    case 'slack':
    case 'google_drive':
    case 'linear':
      content = <ProviderMark provider={artifact.type} className="h-4 w-4" />;
      break;
    default:
      content = <Link2 className="h-4 w-4 text-gray-500 dark:text-gray-400" />;
  }
  return (
    <a
      href={artifact.url}
      target="_blank"
      rel="noopener noreferrer"
      onClick={(e) => e.stopPropagation()}
      title={artifact.label ?? artifact.type}
      className="flex items-center justify-center h-6 w-6 rounded-md bg-gray-100 hover:bg-gray-200 dark:bg-white/5 dark:hover:bg-white/10 transition-colors"
    >
      {content}
    </a>
  );
}

// ── MetaRow ───────────────────────────────────────────────────────────────────

function MetaRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex items-center justify-between py-1.5">
      <span className="text-meta font-semibold uppercase tracking-wider text-gray-400 dark:text-gray-500">
        {label}
      </span>
      <div className="text-meta text-gray-700 dark:text-gray-300">{children}</div>
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────

export function TicketCard({ ticket, viewMode = 'card', onClick }: TicketCardProps) {
  const cfg = STATUS_CONFIG[ticket.status] ?? STATUS_CONFIG.planning;
  const { Icon: StatusIcon } = cfg;
  const typeBadge = getTypeBadge(ticket.type);

  // ── List view — horizontal row, no split ────────────────────────────────────
  if (viewMode === 'list') {
    return (
      <div
        onClick={onClick}
        className="flex items-center gap-3 px-4 py-4 cursor-pointer hover:bg-gray-50 dark:hover:bg-white/[0.03] transition-colors"
      >
        <span className={`inline-flex items-center gap-1 text-meta font-semibold px-2 py-0.5 rounded-full border flex-shrink-0 ${cfg.listBadge}`}>
          <StatusIcon className="h-2.5 w-2.5" />
          {cfg.label}
        </span>

        <span className="text-title text-gray-900 dark:text-white flex-1 truncate">
          {ticket.title}
        </span>

        <span className={`text-meta px-1.5 py-0.5 rounded-full border flex-shrink-0 font-medium ${typeBadge.classes}`}>
          {typeBadge.label}
        </span>

        {ticket.priority != null && ticket.priority > 0 && PRIORITY_BADGE[ticket.priority] && (
          <span className={`text-meta px-1.5 py-0.5 rounded-full border flex-shrink-0 font-medium ${PRIORITY_BADGE[ticket.priority]}`}>
            {PRIORITY_LABELS[ticket.priority]}
          </span>
        )}

        {ticket.dueDate && (
          <span className="text-meta text-gray-400 flex-shrink-0 flex items-center gap-0.5">
            <Calendar className="h-4 w-4" />
            {ticket.dueDate}
          </span>
        )}

        {ticket.assigneeName && (
          <Avatar user={{ fullName: ticket.assigneeName, profilePictureUrl: null }} size="sm" />
        )}
      </div>
    );
  }

  // ── Large view — split: colored header + white/dark body ────────────────────
  if (viewMode === 'large') {
    const suggestedActions = ticket.suggestedActions ?? [];
    const hasMetadata =
      ticket.assigneeName ||
      (ticket.priority != null && ticket.priority > 0) ||
      ticket.dueDate ||
      ticket.estimate != null ||
      (ticket.labels ?? []).length > 0;

    return (
      <motion.div
        whileHover={{ scale: 1.005 }}
        transition={{ duration: 0.15 }}
        onClick={onClick}
        className="rounded-xl border border-gray-200 dark:border-white/[0.08] shadow-sm hover:shadow-md transition-shadow cursor-pointer overflow-hidden"
      >
        {/* Colored header */}
        <div className={`px-5 pt-5 pb-4 ${cfg.headerBg}`}>
          <div className="flex items-center justify-between mb-2.5">
            <span className={`inline-flex items-center gap-1.5 text-meta font-semibold px-2.5 py-1 rounded-full border ${cfg.headerBadge}`}>
              <StatusIcon className="h-4 w-4" />
              {cfg.label}
            </span>
            <span className={`text-meta px-1.5 py-0.5 rounded-full border font-medium ${typeBadge.classes}`}>
              {typeBadge.label}
            </span>
          </div>
          <h4 className="text-title text-gray-900 dark:text-white leading-snug">
            {ticket.title}
          </h4>
          {(ticket.aiSummary ?? ticket.description) && (
            <Markdown
              size="xs"
              className={`mt-1.5 ${ticket.aiSummary ? 'text-gray-600 dark:text-gray-300 italic' : 'text-gray-600 dark:text-gray-300'}`}
            >
              {ticket.aiSummary ?? ticket.description ?? ''}
            </Markdown>
          )}
        </div>

        {/* White / dark body */}
        <div className={`px-5 py-4 bg-white dark:bg-bg-surface border-t ${cfg.divider}`}>
          {/* Metadata rows */}
          {hasMetadata && (
            <div className="divide-y divide-gray-100 dark:divide-white/5 mb-4">
              {ticket.assigneeName && (
                <MetaRow label="Assignee">
                  <div className="flex items-center gap-1.5">
                    <Avatar user={{ fullName: ticket.assigneeName, profilePictureUrl: null }} size="sm" />
                    <span>{ticket.assigneeName}</span>
                  </div>
                </MetaRow>
              )}
              {ticket.priority != null && ticket.priority > 0 && (
                <MetaRow label="Priority">
                  <span className={`inline-flex text-meta px-1.5 py-0.5 rounded-full border font-medium ${PRIORITY_BADGE[ticket.priority]}`}>
                    {ticket.priorityLabel ?? PRIORITY_LABELS[ticket.priority]}
                  </span>
                </MetaRow>
              )}
              {ticket.dueDate && (
                <MetaRow label="Due">
                  <span className="flex items-center gap-1">
                    <Calendar className="h-4 w-4 text-gray-400" />
                    {ticket.dueDate}
                  </span>
                </MetaRow>
              )}
              {ticket.estimate != null && (
                <MetaRow label="Estimate">{ticket.estimate} pts</MetaRow>
              )}
              {(ticket.labels ?? []).length > 0 && (
                <MetaRow label="Labels">
                  <div className="flex flex-wrap gap-1 justify-end">
                    {(ticket.labels ?? []).map(label => (
                      <span key={label} className="text-meta px-1.5 py-0.5 rounded-full bg-gray-100 dark:bg-white/5 text-gray-600 dark:text-gray-400 border border-gray-200 dark:border-white/10">
                        {label}
                      </span>
                    ))}
                  </div>
                </MetaRow>
              )}
            </div>
          )}

          {/* Recent comments — distinct entries, collapsed excerpt,
              expandable in place (CollapsibleText stops propagation so
              expanding does not also open the ticket). */}
          {normalizeComments(ticket.recentComments).length > 0 && (
            <div className="flex items-start gap-2 mb-4 px-3 py-2 rounded-lg bg-gray-50 dark:bg-white/[0.03] border border-gray-100 dark:border-white/5">
              <MessageCircle className="h-4 w-4 text-gray-400 flex-shrink-0 mt-0.5" />
              <CollapsibleText key={ticket.ticketId} collapsedLines={2} className="min-w-0 flex-1">
                <div className="space-y-1">
                  {normalizeComments(ticket.recentComments).map((c, i) => (
                    <div key={i} className="text-gray-500 dark:text-gray-400">
                      {c.author && (
                        <span className="text-meta font-medium text-gray-600 dark:text-gray-300">{c.author}: </span>
                      )}
                      <Markdown size="xs" className="inline-block align-top">
                        {c.text}
                      </Markdown>
                    </div>
                  ))}
                </div>
              </CollapsibleText>
            </div>
          )}

          {/* Artifacts */}
          {(ticket.artifacts ?? []).length > 0 && (
            <div className="flex items-center gap-1.5 mb-4">
              {(ticket.artifacts ?? []).map((a, i) => <ArtifactIcon key={i} artifact={a} />)}
            </div>
          )}

          {/* AI-generated suggested actions */}
          {suggestedActions.length > 0 && (
            <div onClick={(e) => e.stopPropagation()}>
              <ActionBar
                actions={suggestedActions}
                ticketId={ticket.ticketId}
                projectId={ticket.projectId}
                completions={ticket.completions}
                max={3}
                compact
              />
            </div>
          )}
        </div>
      </motion.div>
    );
  }

  // ── Card view (default) — split: colored header + white/dark footer ──────────
  return (
    <motion.div
      whileHover={{ scale: 1.01 }}
      transition={{ duration: 0.15 }}
      onClick={onClick}
      className="rounded-xl border border-gray-200 dark:border-white/[0.08] shadow-sm hover:shadow-md transition-shadow cursor-pointer overflow-hidden"
    >
      {/* Colored header */}
      <div className={`px-5 pt-5 pb-4 ${cfg.headerBg}`}>
        <div className="flex items-center justify-between mb-2.5">
          <span className={`inline-flex items-center gap-1 text-meta font-semibold px-2 py-0.5 rounded-full border ${cfg.headerBadge}`}>
            <StatusIcon className="h-2.5 w-2.5" />
            {cfg.label}
          </span>
          <span className={`text-meta px-1.5 py-0.5 rounded-full border font-medium ${typeBadge.classes}`}>
            {typeBadge.label}
          </span>
        </div>
        <h4 className="text-title text-gray-900 dark:text-white leading-snug">
          {ticket.title}
        </h4>
      </div>

      {/* White / dark footer */}
      <div className={`px-5 py-3 bg-white dark:bg-bg-surface border-t ${cfg.divider} flex items-center justify-between`}>
        <div className="flex items-center gap-1">
          {(ticket.artifacts ?? []).map((a, i) => <ArtifactIcon key={i} artifact={a} />)}
        </div>
        <div className="flex items-center gap-2">
          {ticket.assigneeName && (
            <Avatar user={{ fullName: ticket.assigneeName, profilePictureUrl: null }} size="sm" />
          )}
          {ticket.priority != null && ticket.priority > 0 && PRIORITY_BADGE[ticket.priority] && (
            <span className={`text-meta px-1.5 py-0.5 rounded-full border font-medium ${PRIORITY_BADGE[ticket.priority]}`}>
              {PRIORITY_LABELS[ticket.priority]}
            </span>
          )}
        </div>
      </div>
    </motion.div>
  );
}
